import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, CheckCircle } from "@phosphor-icons/react";

/**
 * A2-467 (operator 2026-10-20) — after an upload "it's very easy to not
 * notice the report has been uploaded and what you need to click". This line
 * names the file that just arrived and the ONE next action: tick it (with a
 * button that does exactly that), and once ticked, where to go next.
 *
 * The live region is always mounted (empty until an upload) so a screen
 * reader announces the message when it appears; and pressing the button,
 * which then disappears, hands focus to the message instead of dropping it
 * to the page.
 */
export const UploadNextStep = ({ run, checked, onInclude }) => {
  const { t } = useTranslation();
  const msgRef = useRef(null);
  const pressed = useRef(false);
  useEffect(() => {
    if (checked && pressed.current) { pressed.current = false; msgRef.current?.focus(); }
  }, [checked]);
  const name = run ? (run.filename || run.id) : "";
  return (
    <div role="status" aria-live="polite" data-testid="upload-next-step-region">
      {run && (
        <div
          ref={msgRef} tabIndex={-1} data-testid="upload-next-step"
          className="mx-4 mt-4 flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-sky-600"
          style={{
            borderColor: "color-mix(in srgb, var(--accent-files) 50%, transparent)",
            background: "color-mix(in srgb, var(--accent-files) 10%, var(--surface-section))",
          }}
        >
          {checked ? (
            <>
              <CheckCircle size={18} weight="bold" style={{ color: "var(--accent-files)" }} aria-hidden="true" />
              <span className="min-w-0 flex-1 text-slate-800 dark:text-slate-100">
                {t("ui.calculate.next_step_included", {
                  name, defaultValue: `“${name}” is included. Next: check the period in part C, then press Run calculation at the bottom.`,
                })}
              </span>
            </>
          ) : (
            <>
              <ArrowDown size={18} weight="bold" style={{ color: "var(--accent-files)" }} aria-hidden="true" />
              <span className="min-w-0 flex-1 text-slate-800 dark:text-slate-100">
                {t("ui.calculate.next_step_tick", {
                  name, defaultValue: `“${name}” is uploaded. Next: include it in this calculation.`,
                })}
              </span>
              <button
                type="button" data-testid="upload-next-step-include"
                onClick={() => { pressed.current = true; onInclude(); }}
                className="rounded-lg bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
              >
                {t("ui.calculate.next_step_include_button", "Include this file")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
